import React from "react";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, Chip, Typography, useTheme, Box, Button, useMediaQuery } from "@mui/material";

const WorkflowTable = ({ workflows, selected, onSelect, onSelectAll, isAllChecked, sortField, sortDirection, onSort, getRelativeTime, onWorkflowClick, onDeleteSelected }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  return (
    <Paper sx={{ 
      borderRadius: { xs: 1.5, sm: 2, md: 2.5, lg: 3, xl: 3 }, 
      overflow: "hidden", 
      bgcolor: theme.palette.background.paper, 
      mb: { xs: 1, sm: 1.5, md: 3, lg: 4, xl: 4 } 
    }}>
      <TableContainer 
        sx={{ 
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: { xs: '400px', sm: '500px', md: '600px', lg: '700px', xl: '800px' },
          '&::-webkit-scrollbar': {
            width: { xs: '6px', sm: '8px', md: '8px', lg: '10px', xl: '10px' },
            height: { xs: '6px', sm: '8px', md: '8px', lg: '10px', xl: '10px' },
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
            borderRadius: '10px',
          },
        }}
      >
        <Table stickyHeader sx={{ minWidth: { xs: 600, sm: 700, md: 1000, lg: 1100, xl: 1100 } }}>
          <TableHead>
            {selected.length > 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ bgcolor: theme.palette.background.paper, p: 0 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    px: { xs: 1, sm: 1.5, md: 1.5, lg: 2, xl: 2 }, 
                    py: { xs: 0.5, sm: 0.75, md: 0.75, lg: 1, xl: 1 } 
                  }}>
                    <Typography sx={{ 
                      fontWeight: 500, 
                      color: theme.palette.text.primary,
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' }
                    }}>
                      {selected.length} selected
                    </Typography>
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size={isXs || isSm ? 'small' : 'medium'}
                      onClick={onDeleteSelected}
                      sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '0.9rem' },
                        px: { xs: 1, sm: 1.5, md: 1.5, lg: 2, xl: 2 },
                        py: { xs: 0.25, sm: 0.5, md: 0.5, lg: 0.625, xl: 0.625 }
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell 
                  padding="checkbox" 
                  sx={{ 
                    bgcolor: theme.palette.background.paper,
                    padding: { xs: '2px', sm: '4px', md: '10px', lg: '12px', xl: '16px' }
                  }}
                >
                  <Checkbox 
                    checked={isAllChecked} 
                    indeterminate={selected.length > 0 && !isAllChecked} 
                    onChange={onSelectAll}
                    size={isXs || isSm ? 'small' : 'medium'}
                    sx={{ 
                      padding: { xs: '2px', sm: '4px', md: '4px', lg: '4px', xl: '4px' }
                    }}
                  />
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: "bold", 
                  color: 'text.primary',
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                  padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                }}>
                  Name
                </TableCell>
                {!isXs && (
                  <TableCell sx={{ 
                    fontWeight: "bold", 
                    color: 'text.primary',
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                    padding: { xs: '4px 8px', sm: '8px 10px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                  }}>
                    Execution
                  </TableCell>
                )}
                <TableCell sx={{ 
                  fontWeight: "bold", 
                  color: 'text.primary',
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                  padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                }}>
                  Status
                </TableCell>
                {!isXs && !isSm && (
                  <TableCell sx={{ 
                    fontWeight: "bold", 
                    color: 'text.primary',
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                    padding: { xs: '4px 8px', sm: '8px 10px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                  }}>
                    Description
                  </TableCell>
                )}
                <TableCell sx={{ 
                  fontWeight: "bold", 
                  color: 'text.primary',
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                  padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                }}>
                  Total enrolled
                </TableCell>
                {!isXs && (
                  <TableCell sx={{ 
                    fontWeight: "bold", 
                    color: 'text.primary',
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                    padding: { xs: '4px 8px', sm: '8px 10px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                  }}>
                    7 day enrollment
                  </TableCell>
                )}
                <TableCell sx={{ 
                  fontWeight: "bold", 
                  color: 'text.primary',
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                  padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                }}>
                  Created
                </TableCell>
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {workflows.length === 0 ? (
              <TableRow>
                <TableCell 
                  colSpan={isXs ? 4 : isSm ? 5 : 8} 
                  align="center"
                  sx={{ 
                    padding: { xs: '16px 8px', sm: '20px 10px', md: '24px 12px', lg: '28px 16px', xl: '32px 16px' }
                  }}
                >
                  <Typography 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' } }}
                  >
                    No workflows found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              workflows.map(wf => (
                <TableRow key={wf.id} hover>
                  <TableCell 
                    padding="checkbox"
                    sx={{ padding: { xs: '2px', sm: '4px', md: '10px', lg: '12px', xl: '16px' } }}
                  >
                    <Checkbox 
                      checked={selected.includes(wf.id)} 
                      onChange={() => onSelect(wf.id)}
                      size={isXs || isSm ? 'small' : 'medium'}
                      sx={{ 
                        padding: { xs: '2px', sm: '4px', md: '4px', lg: '4px', xl: '4px' }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ 
                    color: 'text.primary',
                    fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                    padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                  }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <span
                        style={{
                          textDecoration: 'underline',
                          color: theme.palette.primary.main,
                          cursor: 'pointer',
                          fontWeight: 500
                        }}
                        onClick={() => onWorkflowClick && onWorkflowClick(wf)}
                      >
                        {wf.name}
                      </span>
                      {wf.mediaExpiry && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: wf.mediaExpiry.status === 'expired'
                              ? theme.palette.error.main
                              : wf.mediaExpiry.status === 'expiring_soon'
                                ? theme.palette.warning.main
                                : theme.palette.text.secondary,
                          }}
                        >
                          {wf.mediaExpiry.status === 'expired'
                            ? '(Media expired – re-upload)'
                            : wf.mediaExpiry.status === 'expiring_soon' && wf.mediaExpiry.daysLeft != null
                              ? `(Expires in ${wf.mediaExpiry.daysLeft} day${wf.mediaExpiry.daysLeft !== 1 ? 's' : ''})`
                              : wf.mediaExpiry.daysLeft != null
                                ? `(Media expires in ${wf.mediaExpiry.daysLeft} days)`
                                : null}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  {!isXs && (
                    <TableCell sx={{ 
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                      padding: { xs: '4px 8px', sm: '8px 10px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                    }}>
                      <Chip
                        label={wf.execution}
                        size="small"
                        sx={{
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? (wf.execution === "Customer facing" ? theme.palette.grey[800] : theme.palette.grey[700])
                              : (wf.execution === "Customer facing" ? theme.palette.grey[200] : theme.palette.grey[300]),
                          color:
                            theme.palette.mode === "dark"
                              ? theme.palette.grey[100]
                              : theme.palette.text.primary,
                          fontWeight: 500,
                          fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem', lg: '0.8rem', xl: '0.875rem' },
                          height: { xs: '20px', sm: '22px', md: '24px', lg: '26px', xl: '28px' }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ 
                    fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                    padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                  }}>
                    <Chip
                      label={wf.status}
                      size="small"
                      sx={{
                        bgcolor:
                          wf.status === "On"
                            ? (theme.palette.mode === "dark"
                                ? theme.palette.success.dark
                                : theme.palette.success.light)
                            : (theme.palette.mode === "dark"
                                ? theme.palette.grey[700]
                                : theme.palette.grey[300]),
                        color:
                          wf.status === "On"
                            ? (theme.palette.mode === "dark"
                                ? theme.palette.success.contrastText
                                : theme.palette.success.dark)
                            : (theme.palette.mode === "dark"
                                ? theme.palette.grey[200]
                                : theme.palette.text.primary),
                        fontWeight: 500,
                        fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.75rem', lg: '0.8rem', xl: '0.875rem' },
                        height: { xs: '18px', sm: '20px', md: '24px', lg: '26px', xl: '28px' }
                      }}
                    />
                  </TableCell>
                  {!isXs && !isSm && (
                    <TableCell sx={{ 
                      color: 'text.primary',
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                      padding: { xs: '4px 8px', sm: '8px 10px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                    }}>
                      {wf.description}
                    </TableCell>
                  )}
                  <TableCell sx={{ 
                    color: 'text.primary',
                    fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                    padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                  }}>
                    {wf.totalEnrolled.toLocaleString()}
                  </TableCell>
                  {!isXs && (
                    <TableCell sx={{ 
                      color: 'text.primary',
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                      padding: { xs: '4px 8px', sm: '8px 10px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                    }}>
                      {wf.sevenDayEnrolled.toLocaleString()}
                    </TableCell>
                  )}
                  <TableCell sx={{ 
                    color: 'text.primary',
                    fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                    padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                  }}>
                    {getRelativeTime(wf.createdAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};



export default WorkflowTable; 